import { useEffect, useState } from "react";
import { ArrowUpRight, Instagram, Play } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

export default function InstagramFeed() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/instagram/feed`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("feed error"))))
      .then((data) => setPosts(data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section className="relative border-t border-white/10 py-28 lg:py-36" data-testid="instagram-feed-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
                <span className="h-px w-10 bg-gold" />
                Latest Posts
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl">
                From the <span className="text-purple">grid.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <a
              href={site.igUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border-2 border-white/25 px-6 py-3 text-xs font-black uppercase tracking-[0.2em] text-white transition-all duration-200 hover:-translate-y-1 hover:border-gold hover:text-gold"
              data-testid="instagram-follow-button"
            >
              <Instagram className="h-4 w-4" />
              Follow {site.igHandle}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </Reveal>
        </div>

        {posts === null && (
          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="instagram-feed-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-square animate-pulse border-2 border-white/10 bg-surface" />
            ))}
          </div>
        )}

        {posts !== null && posts.length === 0 && (
          <Reveal delay={0.2}>
            <a
              href={site.igUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-14 flex flex-col items-start justify-between gap-6 border-2 border-dashed border-purple/50 bg-[#0D0714] p-10 transition-colors duration-200 hover:border-purple sm:flex-row sm:items-center"
              data-testid="instagram-feed-fallback"
            >
              <div>
                <p className="font-display text-xl font-black uppercase tracking-tight text-white sm:text-2xl">
                  Fresh posts land here automatically.
                </p>
                <p className="mt-3 max-w-md text-sm font-medium leading-relaxed text-white/60">
                  The feed is being connected to {site.igHandle}. Until then,
                  catch every session, result, and race-week insight on Instagram.
                </p>
              </div>
              <span className="flex items-center gap-3 border-2 border-purple bg-purple px-6 py-4 text-sm font-black uppercase tracking-[0.15em] text-white transition-all duration-200 group-hover:shadow-[0_0_25px_rgba(168,50,255,0.6)]">
                <Instagram className="h-4 w-4" />
                Open Instagram
              </span>
            </a>
          </Reveal>
        )}

        {posts !== null && posts.length > 0 && (
          <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4" data-testid="instagram-feed-grid">
            {posts.slice(0, 8).map((post, i) => {
              const image = post.media_type === "VIDEO" ? post.thumbnail_url : post.media_url;
              return (
                <Reveal key={post.id} delay={i * 0.06}>
                  <a
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative block aspect-square overflow-hidden border-2 border-white/10 transition-colors duration-200 hover:border-purple"
                    data-testid={`instagram-post-${i}`}
                  >
                    {image && (
                      <img
                        src={image}
                        alt={post.caption ? post.caption.slice(0, 120) : "Instagram post"}
                        loading="lazy"
                        className="h-full w-full object-cover grayscale-[35%] transition-all duration-300 group-hover:scale-105 group-hover:grayscale-0"
                      />
                    )}
                    {post.media_type === "VIDEO" && (
                      <span className="absolute right-3 top-3 bg-black/70 p-1.5">
                        <Play className="h-3.5 w-3.5 text-gold" fill="currentColor" />
                      </span>
                    )}
                    <span className="absolute inset-x-0 bottom-0 translate-y-full bg-purple/90 px-3 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-white transition-transform duration-300 group-hover:translate-y-0">
                      View Post
                    </span>
                  </a>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
